"use client";

import { useState, useEffect } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  currentUser: {
    username?: string;
    bio?: string;
    showPlugins?: boolean;
    showMarketplaceItems?: boolean;
    showOrganizations?: boolean;
    showServers?: boolean;
    showMemberSince?: boolean;
  };
}

export function EditProfileModal({ open, onClose, currentUser }: EditProfileModalProps) {
  const updateProfileAndSync = useAction(api.profileActions.updateProfileAndSync);

  const [username, setUsername] = useState(currentUser.username ?? "");
  const [bio, setBio] = useState(currentUser.bio ?? "");
  const [showPlugins, setShowPlugins] = useState(currentUser.showPlugins !== false);
  const [showMarketplaceItems, setShowMarketplaceItems] = useState(currentUser.showMarketplaceItems !== false);
  const [showOrganizations, setShowOrganizations] = useState(currentUser.showOrganizations !== false);
  const [showServers, setShowServers] = useState(currentUser.showServers !== false);
  const [showMemberSince, setShowMemberSince] = useState(currentUser.showMemberSince !== false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setUsername(currentUser.username ?? "");
      setBio(currentUser.bio ?? "");
      setShowPlugins(currentUser.showPlugins !== false);
      setShowMarketplaceItems(currentUser.showMarketplaceItems !== false);
      setShowOrganizations(currentUser.showOrganizations !== false);
      setShowServers(currentUser.showServers !== false);
      setShowMemberSince(currentUser.showMemberSince !== false);
      setError(null);
    }
  }, [open, currentUser]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateProfileAndSync({
        username: username.trim() || undefined,
        bio: bio.trim() || undefined,
        showPlugins,
        showMarketplaceItems,
        showOrganizations,
        showServers,
        showMemberSince,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <Card className="relative z-10 w-full max-w-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Edit Profile</CardTitle>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted transition-colors hover:text-foreground"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="edit-username" className="text-sm font-medium">
              Username
            </label>
            <Input
              id="edit-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your_username"
            />
            <p className="text-xs text-muted">
              3-20 chars. Letters, numbers, underscores, and hyphens only.
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="edit-bio" className="text-sm font-medium">
              Bio
            </label>
            <textarea
              id="edit-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 300))}
              placeholder="Tell others about yourself..."
              rows={3}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
            />
            <p className="text-xs text-muted">{bio.length}/300</p>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium">Visibility Settings</h3>
            <div className="space-y-3">
              <ToggleRow label="Show Plugins" checked={showPlugins} onChange={setShowPlugins} />
              <ToggleRow label="Show Marketplace Items" checked={showMarketplaceItems} onChange={setShowMarketplaceItems} />
              <ToggleRow label="Show Organizations" checked={showOrganizations} onChange={setShowOrganizations} />
              <ToggleRow label="Show Servers" checked={showServers} onChange={setShowServers} />
              <ToggleRow label="Show Member Since" checked={showMemberSince} onChange={setShowMemberSince} />
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-primary" : "bg-foreground/20"
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </button>
    </div>
  );
}
